import React, { useState } from "react";
import { fuzzy } from "fast-fuzzy";
import Breadcrumbs from "./breadcrumbs";
import useGuidesAndTemplates from "@/hooks/documents/useGuidesAndTemplates";
import useDownloadGuideOrTemplate from "@/hooks/documents/useDownloadGuideOrTemplate";
import { ItemList, ItemListRow, ItemListColumn } from "@/components/itemList";
import CustomFileIcon from "@/dumb-components/shared/file-icon/file-icon";
import LoadingPanel from "@/components/loadingPanel";
import i18n from "@/i18n";
import { cn } from "./utils";

const { t } = i18n;

const GuidesAndTemplatesList = () => {
  const { data: guidesAndTemplatesQuery, isLoading } = useGuidesAndTemplates();
  const guidesAndTemplates = guidesAndTemplatesQuery?.data;

  const { mutateAsync: downloadGuideOrTemplate } = useDownloadGuideOrTemplate();

  const [sorting, setSorting] = useState(["name", false]);
  const [filtering, setFiltering] = useState({});

  const guidesAndTemplatesFiltered = guidesAndTemplates?.filter((item) =>
    filtering.value ? fuzzy(filtering.value, item.path) > 0.8 : true,
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="h-[50px]">
          <Breadcrumbs items={[{ label: t("guides_and_templates.title") }]} />
        </div>
        <div className="rounded-lg border border-edge bg-background p-4 shadow">
          <LoadingPanel />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="h-[50px]">
        <Breadcrumbs items={[{ label: t("guides_and_templates.title") }]} />
      </div>

      <div>
        <ItemList
          columns={[
            { id: "name", label: t("generic.name"), isSortable: true },
            {
              id: "tags",
              label: t("company.documents.tags.label.category"),
              isSortable: false,
            },
          ]}
          columnSizing="1fr auto"
          sorting={sorting}
          onSort={setSorting}
          onFilter={setFiltering}
          zeroStateRenderer={() => t("guides_and_templates.list.zero_state")}
        >
          {guidesAndTemplatesFiltered
            ?.toSorted((a, b) => {
              const [sortBy, sortDesc] = sorting;
              const x = sortDesc ? b : a;
              const y = sortDesc ? a : b;
              switch (sortBy) {
                case "name":
                  return x.name?.localeCompare(y.name);
                default:
                  return 0;
              }
            })
            ?.map((item) => {
              const documentParts = item.name.split(".");
              const ext = documentParts[documentParts.length - 1];

              const handleFileClick = async () => {
                const url = await downloadGuideOrTemplate(item.path);

                if (url) {
                  window.open(url, "_blank");
                }
              };

              // Parse tags from customMetadata.tags
              const tags = item.customMetadata?.tags
                ? item.customMetadata.tags
                    .split(",")
                    .map((tag) => tag.trim())
                    .filter((tag) => tag.length > 0)
                    .map((tag) => ({
                      id: tag,
                      name: t(`guides_and_templates.tags.${tag}`),
                      variant: "ghost",
                    }))
                : [];

              return (
                <ItemListRow key={item.path} onClick={handleFileClick}>
                  <ItemListColumn
                    truncate
                    title={item.customMetadata?.description}
                  >
                    <div
                      className={cn("truncate", {
                        "animate-[wiggle_1s_ease-in-out_infinite]":
                          item.path ===
                          "Handbok i Bolagsstyrning, Edition 1.pdf",
                      })}
                    >
                      <div className="flex items-center gap-3 truncate">
                        <div className="w-6 flex-shrink-0">
                          <CustomFileIcon ext={ext} />
                        </div>
                        <span className="truncate">{item.name}</span>
                      </div>
                    </div>
                  </ItemListColumn>
                  <ItemListColumn.Badges data={tags} maxCount={3} />
                </ItemListRow>
              );
            })}
        </ItemList>
      </div>
    </div>
  );
};

export default GuidesAndTemplatesList;
